import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Share2, Wallet, Smartphone, CheckCircle2, AlertCircle, Printer } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { QRCodeSVG } from "qrcode.react";
import Barcode from "react-barcode";

// Species configuration
const SPECIES_CONFIG = {
  dog: { name: "Cane", category: "da_compagnia", emoji: "🐕", anagrafe: "CANINA" },
  cat: { name: "Gatto", category: "da_compagnia", emoji: "🐱", anagrafe: "FELINA" },
  horse: { name: "Cavallo", category: "equide", emoji: "🐴", anagrafe: "EQUINA" },
  rabbit: { name: "Coniglio", category: "da_compagnia", emoji: "🐰", anagrafe: "ANIMALI" },
  bird: { name: "Uccello", category: "da_compagnia", emoji: "🦜", anagrafe: "ANIMALI" },
  fish: { name: "Pesce", category: "da_compagnia", emoji: "🐠", anagrafe: "ANIMALI" },
  hamster: { name: "Criceto", category: "da_compagnia", emoji: "🐹", anagrafe: "ANIMALI" },
  turtle: { name: "Tartaruga", category: "da_compagnia", emoji: "🐢", anagrafe: "ANIMALI" },
  ferret: { name: "Furetto", category: "da_compagnia", emoji: "🦡", anagrafe: "ANIMALI" },
  guinea_pig: { name: "Cavia", category: "da_compagnia", emoji: "🐹", anagrafe: "ANIMALI" },
  other: { name: "Animale", category: "da_compagnia", emoji: "🐾", anagrafe: "ANIMALI" }
};

const CATEGORY_LABELS = {
  da_compagnia: "DA COMPAGNIA",
  equide: "EQUIDI",
  domestici: "DOMESTICI"
};

/**
 * PetPassport - Documento Ufficiale Digitale
 * Design stile Passaporto Europeo / Anagrafe Digitale
 */
export default function PetPassport({ pet, owner, vaccines = [], open, onClose }) {
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeView, setActiveView] = useState("front"); // "front" or "back"

  if (!pet) return null;

  const speciesConfig = SPECIES_CONFIG[pet.species] || SPECIES_CONFIG.other;
  const categoryLabel = CATEGORY_LABELS[speciesConfig.category] || "DA COMPAGNIA";

  // Check if document is "official" (has required fields)
  const isOfficial = !!(
    pet.name &&
    pet.microchip &&
    owner?.name &&
    owner?.fiscal_code &&
    owner?.address
  );

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/D";
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  // Format breed
  const formatBreed = (breed) => {
    if (!breed) return "NON SPECIFICATA";
    return breed.toUpperCase();
  };

  // Format sex
  const formatSex = (sex) => {
    if (!sex) return "N/D";
    const sexMap = {
      male: "MASCHIO / MALE",
      female: "FEMMINA / FEMALE",
      M: "MASCHIO / MALE",
      F: "FEMMINA / FEMALE"
    };
    return sexMap[sex] || sex.toUpperCase();
  };

  // Generate passport number - Use EU passport number if available
  const passportNumber = pet.eu_passport_number 
    ? pet.eu_passport_number
    : pet.microchip 
      ? `IT ${pet.microchip}`
      : `ZMP${pet.pet_id?.slice(-8)?.toUpperCase() || "00000000"}`;

  // Generate MRZ line (Machine Readable Zone) - EU standard format
  const generateMRZ = () => {
    const name = (pet.name || "UNKNOWN").toUpperCase().replace(/[^A-Z]/g, "").padEnd(10, "<").slice(0, 10);
    const microchip = (pet.microchip || "000000000000000").replace(/[^0-9]/g, "").slice(0, 15);
    const species = (pet.species || "DOG").toUpperCase().slice(0, 3);
    return `P<ITA${name}<<${species}<<${microchip}<<<<<<`;
  };

  // Find rabies vaccine - check both vaccines array and pet direct fields
  const rabiesVaccine = vaccines.find(v => {
    const name = (v.name || v.title || v.vaccine_name || '').toLowerCase();
    return name.includes("rabbia") || name.includes("antirabbica") || name.includes("rabies") || v.is_rabies;
  });

  // Also check pet's direct rabies fields (from EU passport OCR)
  const rabiesDate = rabiesVaccine?.date_administered || pet.rabies_vaccine_date;
  const rabiesExpiry = rabiesVaccine?.expiry_date || pet.rabies_vaccine_expiry;
  const rabiesVaccineName = rabiesVaccine?.name || pet.rabies_vaccine_name;
  const rabiesBatch = rabiesVaccine?.batch_number || pet.rabies_vaccine_batch;
  const rabiesVet = rabiesVaccine?.vet_name || pet.rabies_vaccine_vet;

  const isRabiesValid = rabiesExpiry && new Date(rabiesExpiry) > new Date();

  // Microchip info
  const microchipLocation = pet.microchip_location || "collo (standard)";
  const microchipDate = pet.microchip_date;

  // QR code data - link to pet profile
  const qrData = `https://www.zampa.one/pet/${pet.share_token || pet.pet_id}`;

  // Barcode value - microchip (15 digits standard)
  const barcodeValue = pet.microchip?.replace(/[^0-9]/g, "") || "000000000000000";

  // Download as PDF (both sides)
  const downloadPDF = async () => {
    if (!frontRef.current || !backRef.current) return;
    setIsGenerating(true);
    
    try {
      // Capture front
      const frontCanvas = await html2canvas(frontRef.current, {
        scale: 3,
        backgroundColor: null,
        logging: false,
        useCORS: true
      });
      
      // Capture back
      const backCanvas = await html2canvas(backRef.current, {
        scale: 3,
        backgroundColor: null,
        logging: false,
        useCORS: true
      });
      
      // Create PDF with both sides
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: [86, 54] // Credit card size
      });
      
      // Add front
      pdf.addImage(frontCanvas.toDataURL("image/png"), "PNG", 0, 0, 86, 54);
      
      // Add back on new page
      pdf.addPage([86, 54], "landscape");
      pdf.addImage(backCanvas.toDataURL("image/png"), "PNG", 0, 0, 86, 54);
      
      pdf.save(`${pet.name}_Passaporto_Zampa.pdf`);
      toast.success("PDF scaricato con successo!");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Errore nella generazione del PDF");
    } finally {
      setIsGenerating(false);
    }
  };

  // Download as Image (current view)
  const downloadImage = async () => {
    const ref = activeView === "front" ? frontRef.current : backRef.current;
    if (!ref) return;
    setIsGenerating(true);
    
    try {
      const canvas = await html2canvas(ref, {
        scale: 3,
        backgroundColor: null,
        logging: false,
        useCORS: true
      });
      
      const link = document.createElement("a");
      link.download = `${pet.name}_Passaporto_${activeView === "front" ? "Fronte" : "Retro"}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Immagine scaricata!");
    } catch (error) {
      console.error("Image generation error:", error);
      toast.error("Errore nel download");
    } finally {
      setIsGenerating(false);
    }
  };

  // Share card
  const shareCard = async () => {
    const ref = activeView === "front" ? frontRef.current : backRef.current;
    if (!ref) return;
    
    try {
      const canvas = await html2canvas(ref, {
        scale: 2,
        backgroundColor: null,
        logging: false,
        useCORS: true
      });
      
      canvas.toBlob(async (blob) => {
        if (navigator.share && blob) {
          const file = new File([blob], `${pet.name}_Passaporto.png`, { type: "image/png" });
          await navigator.share({
            title: `Passaporto di ${pet.name}`,
            text: `Documento digitale Zampa di ${pet.name}`,
            files: [file]
          });
        } else {
          downloadImage();
        }
      });
    } catch (error) {
      downloadImage();
    }
  };

  // Wallet placeholders
  const addToAppleWallet = () => toast.info("Apple Wallet in arrivo!");
  const addToGoogleWallet = () => toast.info("Google Wallet in arrivo!");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[480px] p-0 rounded-3xl border-0 overflow-hidden max-h-[95vh] overflow-y-auto bg-gradient-to-b from-slate-100 to-slate-200">
        <div className="p-5">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              {isOfficial ? (
                <CheckCircle2 className="text-emerald-500" size={20} />
              ) : (
                <AlertCircle className="text-amber-500" size={20} />
              )}
              Passaporto Digitale {isOfficial ? "(Ufficiale)" : "(Non verificato)"}
            </DialogTitle>
          </DialogHeader>

          {/* Card View Toggle */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={activeView === "front" ? "default" : "outline"}
              onClick={() => setActiveView("front")}
              className={`flex-1 h-9 rounded-xl text-sm ${activeView === "front" ? "bg-[#1a365d]" : ""}`}
            >
              Fronte
            </Button>
            <Button
              variant={activeView === "back" ? "default" : "outline"}
              onClick={() => setActiveView("back")}
              className={`flex-1 h-9 rounded-xl text-sm ${activeView === "back" ? "bg-[#1a365d]" : ""}`}
            >
              Retro
            </Button>
          </div>

          {/* FRONT SIDE - Passaporto Europeo Style */}
          <div className={`flex justify-center mb-5 ${activeView !== "front" ? "hidden" : ""}`}>
            <div 
              ref={frontRef}
              className="w-full max-w-[380px] aspect-[1.586/1] rounded-xl overflow-hidden shadow-2xl"
              style={{
                background: "linear-gradient(135deg, #e8ecef 0%, #d1d5db 50%, #e5e7eb 100%)"
              }}
            >
              {/* Blue Header */}
              <div 
                className="px-4 py-2 flex items-center justify-between"
                style={{
                  background: "linear-gradient(90deg, #1a365d 0%, #2c5282 100%)"
                }}
              >
                <div className="flex items-center gap-2">
                  {/* Italian Emblem */}
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">🇮🇹</span>
                  </div>
                  <div className="text-white">
                    <p className="text-[10px] font-semibold tracking-wide">REPUBBLICA</p>
                    <p className="text-[10px] font-semibold tracking-wide">ITALIANA</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold text-sm tracking-wide">PASSAPORTO EUROPEO</p>
                  <p className="text-white/80 text-[10px] tracking-wide">ANIMALE {categoryLabel}</p>
                </div>
              </div>

              {/* Main Content */}
              <div className="p-3 flex gap-3">
                {/* Pet Photo */}
                <div className="flex-shrink-0">
                  <div 
                    className="w-24 h-28 rounded-lg overflow-hidden border-2 border-slate-300 shadow-inner"
                    style={{ background: "linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)" }}
                  >
                    {pet.photo_url ? (
                      <img 
                        src={pet.photo_url} 
                        alt={pet.name}
                        className="w-full h-full object-cover"
                        crossOrigin="anonymous"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl">
                        {speciesConfig.emoji}
                      </div>
                    )}
                  </div>
                  {/* Pet Name under photo */}
                  <div className="mt-1 text-center">
                    <p className="text-[#1a365d] font-bold text-sm uppercase tracking-wide">{pet.name}</p>
                  </div>
                </div>

                {/* Pet Details */}
                <div className="flex-1 space-y-1">
                  <div>
                    <p className="text-slate-500 text-[9px] italic">Razza / Breed:</p>
                    <p className="text-slate-800 font-bold text-xs">{formatBreed(pet.breed)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-[9px] italic">Sesso / Sex:</p>
                    <p className="text-slate-800 font-semibold text-xs">{formatSex(pet.sex)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-[9px] italic">Data di Nascita:</p>
                    <p className="text-slate-800 font-semibold text-xs">{formatDate(pet.birth_date)}</p>
                  </div>
                  <div className="pt-1 flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-slate-200 flex items-center justify-center">
                      <span className="text-[8px]">📟</span>
                    </div>
                    <div>
                      <p className="text-slate-500 text-[8px]">Microchip:</p>
                      <p className="text-slate-800 font-mono font-bold text-[10px]">{pet.microchip || "NON REGISTRATO"}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-slate-500 text-[8px]">N. Passaporto / Passport No.</p>
                    <div className="flex items-center gap-1">
                      <div className="flex h-3">
                        <div className="w-1 bg-[#009246] rounded-l" />
                        <div className="w-1 bg-white border-y border-slate-300" />
                        <div className="w-1 bg-[#CE2B37] rounded-r" />
                      </div>
                      <span className="text-[#1a365d] font-mono font-bold text-[10px]">{passportNumber}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* MRZ Zone */}
              <div className="bg-slate-200 px-3 py-1.5 border-t border-slate-300">
                <p className="font-mono text-[10px] text-slate-700 tracking-widest truncate">
                  {generateMRZ()}
                </p>
              </div>
            </div>
          </div>

          {/* BACK SIDE - Anagrafe Digitale Style */}
          <div className={`flex justify-center mb-5 ${activeView !== "back" ? "hidden" : ""}`}>
            <div 
              ref={backRef}
              className="w-full max-w-[380px] aspect-[1.586/1] rounded-xl overflow-hidden shadow-2xl"
              style={{
                background: "linear-gradient(135deg, #f0f4f8 0%, #d9e2ec 50%, #e4e9ef 100%)"
              }}
            >
              {/* Blue Header */}
              <div 
                className="px-4 py-2 flex items-center justify-between"
                style={{
                  background: "linear-gradient(90deg, #1a365d 0%, #2c5282 100%)"
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">🇮🇹</span>
                  </div>
                  <div className="text-white">
                    <p className="text-[10px] font-semibold tracking-wide">REPUBBLICA</p>
                    <p className="text-[10px] font-semibold tracking-wide">ITALIANA</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold text-sm tracking-wide">ANAGRAFE {speciesConfig.anagrafe} DIGITALE</p>
                </div>
              </div>

              {/* Owner Info */}
              <div className="p-3 space-y-2">
                <div>
                  <p className="text-[#1a365d] font-bold text-base uppercase tracking-wide">
                    {owner?.name || "PROPRIETARIO NON REGISTRATO"}
                  </p>
                </div>

                {owner?.fiscal_code && (
                  <div>
                    <p className="text-slate-500 text-[9px]">Codice Fiscale:</p>
                    <p className="text-slate-800 font-mono font-semibold text-xs tracking-wide">{owner.fiscal_code}</p>
                  </div>
                )}

                {owner?.address && (
                  <div className="flex items-start gap-1.5">
                    <span className="text-[#1a365d] text-sm">🏠</span>
                    <div>
                      <p className="text-slate-700 text-xs leading-tight">{owner.address}</p>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  {owner?.phone && (
                    <div className="flex items-center gap-1">
                      <span className="text-[#1a365d] text-xs">📞</span>
                      <p className="text-slate-700 text-xs">{owner.phone}</p>
                    </div>
                  )}
                  {owner?.email && (
                    <div className="flex items-center gap-1">
                      <span className="text-[#1a365d] text-xs">✉️</span>
                      <p className="text-slate-700 text-xs truncate max-w-[140px]">{owner.email}</p>
                    </div>
                  )}
                </div>

                {/* QR and Barcode Row */}
                <div className="flex items-end justify-between pt-1">
                  <div className="flex-1">
                    {/* Barcode */}
                    <div className="bg-white rounded p-1 inline-block">
                      <Barcode 
                        value={barcodeValue}
                        width={1}
                        height={30}
                        fontSize={8}
                        margin={0}
                        displayValue={true}
                      />
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-1.5 shadow-sm">
                    <QRCodeSVG 
                      value={qrData}
                      size={60}
                      level="M"
                      includeMargin={false}
                    />
                  </div>
                </div>

                {/* Footer Note */}
                <p className="text-slate-500 text-[7px] leading-tight mt-1">
                  Per informazioni complete scannerizzare il codice QR o il codice a barre.
                  Lettura autorizzata solo da servizi veterinari autorizzati.
                </p>
              </div>

              {/* Verification Badge */}
              {isOfficial && (
                <div className="absolute bottom-2 right-2">
                  <div className="bg-emerald-500 text-white rounded-full p-1">
                    <CheckCircle2 size={12} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Official Status Notice */}
          {!isOfficial && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <p className="text-amber-800 text-xs flex items-start gap-2">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Documento non verificato.</strong> Per renderlo ufficiale aggiungi: 
                  {!pet.microchip && " microchip,"}
                  {!owner?.fiscal_code && " codice fiscale,"}
                  {!owner?.address && " indirizzo"}
                </span>
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={downloadPDF}
                disabled={isGenerating}
                className="h-11 bg-[#1a365d] hover:bg-[#2c5282] rounded-xl text-sm"
                data-testid="download-pdf-btn"
              >
                <Download size={16} className="mr-2" />
                Scarica PDF
              </Button>
              <Button
                onClick={downloadImage}
                disabled={isGenerating}
                variant="outline"
                className="h-11 rounded-xl text-sm border-[#1a365d] text-[#1a365d]"
                data-testid="download-image-btn"
              >
                <Printer size={16} className="mr-2" />
                Immagine
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={addToAppleWallet}
                variant="outline"
                className="h-11 rounded-xl bg-black text-white hover:bg-gray-800 border-0 text-sm"
              >
                <Wallet size={16} className="mr-2" />
                Apple Wallet
              </Button>
              <Button
                onClick={addToGoogleWallet}
                variant="outline"
                className="h-11 rounded-xl bg-[#4285F4] text-white hover:bg-[#3367D6] border-0 text-sm"
              >
                <Smartphone size={16} className="mr-2" />
                Google Wallet
              </Button>
            </div>

            <Button
              onClick={shareCard}
              variant="outline"
              className="w-full h-11 rounded-xl text-sm"
              data-testid="share-passport-btn"
            >
              <Share2 size={16} className="mr-2" />
              Condividi Passaporto
            </Button>
          </div>

          <p className="text-[9px] text-center text-slate-500 mt-3">
            Documento digitale generato da Zampa. Per viaggi internazionali è necessario il passaporto ufficiale ASL.
            {isOfficial && " Questo documento è verificato e contiene tutti i dati richiesti."}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
